import React, { useEffect, useState, useMemo } from 'react';
import 'styled-components/macro';
import { AuthContext } from 'context/authContext';
import Button from 'components/atoms/Button';
import Grid from 'components/atoms/Grid';

import adminService from 'services/admin.service';
import { LangConverter } from 'helpers/common';
import Field from 'components/molecules/Field';
import Form, { useForm } from 'components/molecules/Form';
import Toast from 'components/molecules/Toast';
import { useContextHook } from 'use-context-hook';
import PermissionSelector from '../PermissionSelector';

function RolesForm({ role, onClose = () => {} }) {
  const [form] = useForm();
  const [state, setState] = useState({});
  const [loading, setLoading] = useState(false);
  const { refetch } = useContextHook(AuthContext, ['refetch']);
  const permissions = useMemo(() => role?.permissions?.map(({ _id }) => _id) ?? [], [role]);
  const { lang } = useContextHook(AuthContext, { lang: 1 });

  useEffect(() => {
    if (role) {
      form.setFieldsValue({
        type: role?.type,
        description: role?.description,
      });
      setState({
        type: role?.type,
        description: role?.description,
        permissions,
      });
    }
  }, [role]);
  const onSubmit = async data => {
    try {
      setLoading(true);
      if (role) {
        await adminService.updateRole(role._id, {
          type: data.type,
          description: data.description,
          permissions: state.permissions,
        });
      } else {
        await adminService.createRole({
          type: data.type,
          description: data.description,
          permissions: state.permissions,
        });
      }
      refetch();
      onClose();
      setLoading(false);
      Toast({
        type: 'success',
        message: LangConverter('Role saved successfully', lang.value),
      });
    } catch (ex) {
      setLoading(false);
      Toast({
        type: 'error',
        message: ex.message,
      });
    }
  };

  return (
    <Form form={form} onSubmit={onSubmit} onTouched={_ => setState(__ => ({ ...__, ..._ }))}>
      <Grid
        xs={1}
        lg={2}
        colGap={20}
        css={`
          align-items: center;
        `}>
        <Form.Item
          sm
          type="text"
          label={LangConverter('Type', lang.value)}
          name="type"
          placeholder={LangConverter('Role Type', lang.value)}
          rules={[
            { required: true, message: LangConverter('Please enter role', lang.value) },
            {
              pattern: /^[A-Z_]+$/,
              message: LangConverter('Role type must be uppercase and only alphabets and _ are allowed', lang.value),
            },
          ]}>
          <Field />
        </Form.Item>

        <Form.Item
          sm
          type="text"
          label={LangConverter('Description', lang.value)}
          name="description"
          placeholder={LangConverter('Description', lang.value)}
          rules={[
            {
              required: true,
              message: LangConverter('Description is required', lang.value),
            },
          ]}>
          <Field />
        </Form.Item>
        <div />
        <PermissionSelector
          permissions={permissions}
          onDone={__ => setState(_ => ({ ..._, permissions: __ }))}
          forRoles
        />
      </Grid>
      <Button
        disabled={!state?.permissions?.length}
        loading={loading}
        type="primary"
        htmlType="submit"
        css={`
          margin-top: 20px;
        `}>
        {LangConverter('Save', lang.value)}
      </Button>
    </Form>
  );
}

export default RolesForm;
